import type { AIWorkoutResponse, SportType, WorkoutSegment, ZoneDefinition } from '@/types';

/** Polar Flow web session API (same origin when logged in on flow.polar.com). */
export const POLAR_FLOW_TRAINING_TARGET_URL = 'https://flow.polar.com/api/trainingtarget';

export type PolarFlowWebPhase = {
  phaseType: 'PHASE';
  name: string;
  /** Present on native Polar UI requests; omitting may break server-side parsing. */
  phaseChangeType: 'AUTOMATIC';
  goalType: 'DURATION';
  /** Null when goal is duration-based (matches Polar web JSON). */
  distance: null;
  duration: string;
  intensityType: 'POWER_ZONES' | 'HEART_RATE_ZONES';
  lowerZone: number;
  upperZone: number;
};

function pad2(n: number): string {
  return String(Math.max(0, Math.min(99, n))).padStart(2, '0');
}

/** Strips `Z` / fractional seconds; keeps `THH:mm:ss` when present, else `THH:mm` (Polar Flow local time). */
export function normalizePolarFlowDatetimeString(datetime: string): string {
  let s = String(datetime || '').trim();
  if (!s) return s;
  s = s.replace(/\.\d+Z?$/i, '').replace(/Z$/i, '');
  const mSec = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})/.exec(s);
  if (mSec) return mSec[1];
  const mMin = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})$/.exec(s);
  if (mMin) return mMin[1];
  return s;
}

/** Independent uniform 0..59 for minute and second (single `getRandomValues` when available). */
function randomMinuteAndSecond0To59(): { minute: number; second: number } {
  if (typeof globalThis.crypto !== 'undefined' && typeof globalThis.crypto.getRandomValues === 'function') {
    const a = new Uint8Array(2);
    globalThis.crypto.getRandomValues(a);
    return { minute: a[0] % 60, second: a[1] % 60 };
  }
  return {
    minute: Math.floor(Math.random() * 60),
    second: Math.floor(Math.random() * 60),
  };
}

/** Default evening hour with random minute + second to avoid Polar `twoTargetsForSameTime` on the same day. */
function polarDefaultTimeLocalWithRandomMinuteSecond(): string {
  const { minute, second } = randomMinuteAndSecond0To59();
  return `18:${pad2(minute)}:${pad2(second)}`;
}

/** Polar Flow expects `HH:MM:SS` duration strings (hours may be 00). */
export function formatPolarFlowDurationHms(seconds: number): string {
  const s = Math.max(60, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${pad2(h)}:${pad2(m)}:${pad2(sec)}`;
}

/** Polar phased targets use 1–5 zone bands; API rejects lowerZone >= upperZone. */
export function normalizePolarZonePair(lower: number, upper: number): { lowerZone: number; upperZone: number } {
  let lo = Math.max(1, Math.min(5, Math.round(lower)));
  let hi = Math.max(1, Math.min(5, Math.round(upper)));
  if (hi <= lo) hi = Math.min(5, lo + 1);
  if (hi <= lo) lo = Math.max(1, hi - 1);
  return { lowerZone: lo, upperZone: hi };
}

function zoneFromPowerPct(
  lowPct: number,
  highPct: number,
  zones?: readonly ZoneDefinition[],
): { lowerZone: number; upperZone: number } {
  const lo = Number.isFinite(lowPct) ? lowPct : 0;
  const hi = Number.isFinite(highPct) ? highPct : lo;
  const mid = (lo + hi) / 2;
  const validZones = (zones ?? []).filter(
    (z) => z && Number.isFinite(z.min) && Number.isFinite(z.max) && z.max > z.min,
  );
  if (validZones.length > 0) {
    const midFrac = mid / 100;
    for (let i = 0; i < validZones.length; i++) {
      const z = validZones[i];
      if (midFrac >= z.min && midFrac <= z.max) {
        const lowerZone = i + 1;
        return normalizePolarZonePair(lowerZone, lowerZone + 1);
      }
    }
    if (midFrac > validZones[validZones.length - 1].max) {
      const n = validZones.length;
      return normalizePolarZonePair(n, n + 1);
    }
  }
  const z = Math.max(1, Math.min(5, Math.round(mid / 25)));
  return normalizePolarZonePair(z, z + 1);
}

function segmentLabel(seg: WorkoutSegment, index: number, language: 'en' | 'pl'): string {
  if (language === 'pl') {
    if (seg.type === 'Warmup') return 'Rozgrzewka';
    if (seg.type === 'Cooldown') return 'Schłodzenie';
    return `Faza ${index + 1}`;
  }
  if (seg.type === 'Warmup') return 'Warmup';
  if (seg.type === 'Cooldown') return 'Cooldown';
  return `Phase ${index + 1}`;
}

/**
 * Builds the JSON body Polar Flow accepts at `POST /api/trainingtarget`
 * (phased power / HR zones — aligned with the product spec).
 *
 * When `timeLocal` is omitted, uses **`18:MM:SS`** with **random minute and second 0–59** so repeated
 * exports on the same day avoid Polar's `twoTargetsForSameTime` clash.
 */
export function buildPolarFlowWebTrainingTargetDto(input: {
  workoutName: string;
  description: string;
  calendarDate: string;
  timeLocal?: string;
  segments: WorkoutSegment[];
  zones?: readonly ZoneDefinition[];
  sport: SportType;
  language?: 'en' | 'pl';
}): Record<string, unknown> {
  const language = input.language ?? 'pl';
  const time = input.timeLocal ?? polarDefaultTimeLocalWithRandomMinuteSecond();
  let datetime = /^\d{2}:\d{2}:\d{2}$/.test(time)
    ? `${input.calendarDate}T${time}`
    : /^\d{2}:\d{2}$/.test(time)
      ? `${input.calendarDate}T${time}`
      : `${input.calendarDate}T${polarDefaultTimeLocalWithRandomMinuteSecond()}`;
  datetime = normalizePolarFlowDatetimeString(datetime);

  /** Polar phased targets: running = 1; cycling uses Polar web cycling id (not 1). */
  const sportId = input.sport === 'bike' ? 18 : 1;
  const list = (input.segments ?? []).filter((s) => {
    if (!s) return false;
    const dur = Number(s.duration);
    if (!Number.isFinite(dur) || dur <= 0) return false;
    if (!Number.isFinite(Number(s.powerLow)) || !Number.isFinite(Number(s.powerHigh))) return false;
    return true;
  });
  const intensityType: PolarFlowWebPhase['intensityType'] =
    input.sport === 'bike' ? 'POWER_ZONES' : 'HEART_RATE_ZONES';

  // Linear PHASE rows only — Polar's `REPEAT` wraps repeated blocks in the editor; our segments are
  // already expanded (e.g. 3×10' as three intervals). Collapsing into REPEAT would need interval-level
  // repeat metadata we do not model; the flat list is valid and matches total duration/power.
  const phases: PolarFlowWebPhase[] = list.map((seg, idx) => {
    const low = Math.round(Number(seg.powerLow) * 100);
    const high = Math.round(Number(seg.powerHigh) * 100);
    const { lowerZone, upperZone } = zoneFromPowerPct(low, high, input.zones);
    return {
      phaseType: 'PHASE',
      name: segmentLabel(seg, idx, language),
      phaseChangeType: 'AUTOMATIC',
      goalType: 'DURATION',
      distance: null,
      duration: formatPolarFlowDurationHms(seg.duration),
      intensityType,
      lowerZone,
      upperZone,
    };
  });

  if (!phases.length) {
    throw new Error('POLAR_NO_PHASES');
  }

  // Single sport block: match Polar Flow web UI keys (including explicit nulls).
  return {
    type: 'PHASED',
    name: input.workoutName.trim().slice(0, 80) || 'Prompt2Train',
    description: input.description.trim().slice(0, 500),
    datetime,
    exerciseTargets: [
      {
        distance: null,
        calories: null,
        phases,
        sportId,
        duration: null,
        id: null,
      },
    ],
  };
}

export function polarDescriptionFromWorkout(ai: AIWorkoutResponse | null, fallback: string): string {
  if (!ai) return fallback;
  const steps = Array.isArray(ai.steps) ? ai.steps : [];
  const parts = steps.slice(0, 8).map((s) => s.work?.intensity ?? '').filter(Boolean);
  const joined = parts.join(' · ');
  return (joined || ai.workoutName || fallback).slice(0, 500);
}
